import { cn } from "@gigpro/ui/tailwind/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type React from "react";
import { CheckCircle, ErrorSign, InfoOutline, Warning } from "../../Icons";

const alertVariants = cva("flex items-center rounded-md font-semibold text-white", {
  variants: {
    severity: {
      info: "bg-info",
      success: "bg-success",
      warning: "bg-warning text-black",
      error: "bg-error",
    },
    size: {
      small: "gap-1 px-1.5 py-0.75 text-small",
      medium: "gap-1.5 p-2 text-normal",
      large: "gap-2 px-3 py-2.5 text-large",
    },
  },
  defaultVariants: {
    severity: "info",
    size: "medium",
  },
});

const iconSizeMap = {
  small: "size-3",
  medium: "size-4",
  large: "size-4",
} as const;

const defaultIcons = {
  info: InfoOutline,
  success: CheckCircle,
  warning: Warning,
  error: ErrorSign,
} as const;

export interface AlertProps extends React.ComponentProps<"div">, VariantProps<typeof alertVariants> {
  action?: React.ReactNode;
}

export const Alert = ({ children, severity, size, action, className, ...props }: AlertProps) => {
  const iconStyle = iconSizeMap[size ?? "medium"];
  const iconClass = cn("shrink-0", iconStyle);
  const IconComponent = defaultIcons[severity ?? "info"];

  return (
    <div role="alert" className={cn(alertVariants({ severity, size }), className)} {...props}>
      <IconComponent className={iconClass} />
      {children}
      {action && <span className="ml-auto">{action}</span>}
    </div>
  );
};
